import { useAppSelector, useAppDispatch } from '@/app/hooks';
import { update as updateMapLayerConfig } from '@/features/mapLayerSlice';
import type { RiskTypeConfig } from '@/views/map/layers/types';
import { LayerType } from '@/views/map/layers/types';
import { useClientTerminology } from '@/views/list/NetworkTerms';
import { Checkbox, Text } from '@mantine/core';
import { Icon } from '@/components/Icon/Icon';
import { useTreeTypes } from '@/features/fieldSlice';
import { getGoogleEventForAnalysis } from '@/features/googleEvents';
import { capitalize } from '@/helpers/stringConversion';
import TagManager from 'react-gtm-module';
import classes from './FallInRiskControl.module.css';

export const FallInRiskControl = () => {
  const dispatch = useAppDispatch();
  const layerConfig = useAppSelector((s) => s.mapLayer);
  const { selectedAnalysis } = useAppSelector((s) => s.analysisMap);
  const intl = useClientTerminology();

  const { showHazardTrees, showDecliningTrees, showStrikeTrees } = layerConfig[
    LayerType.trees
  ] as RiskTypeConfig;

  const title = intl.formatMessage({ id: 'list.fallInHealthAnalysisView' });
  const description = `${intl.formatMessage({ id: 'map.layerManager.treesDescription' })}:`;

  const HAZARD_TREES = 'hazard_trees';
  const DECLINING_TREES = 'declining_trees';
  const STRIKE_TREES = 'strike_trees';

  const { getClientTerm } = useClientTerminology();
  const treeTypes = useTreeTypes();

  const setOption = (v: boolean, layerType?: string) => {
    if (layerType) {
      TagManager.dataLayer({
        dataLayer: {
          event: getGoogleEventForAnalysis(
            selectedAnalysis,
            `${layerType}_${v ? 'on' : 'off'}`
          ),
        },
      });
      dispatch(
        updateMapLayerConfig(
          (() => {
            switch (layerType) {
              case HAZARD_TREES:
                return {
                  [LayerType.trees]: {
                    ...layerConfig[LayerType.trees],
                    showHazardTrees: v,
                  },
                };
              case DECLINING_TREES:
                return {
                  [LayerType.trees]: {
                    ...layerConfig[LayerType.trees],
                    showDecliningTrees: v,
                  },
                };
              case STRIKE_TREES:
                return {
                  [LayerType.trees]: {
                    ...layerConfig[LayerType.trees],
                    showStrikeTrees: v,
                  },
                };
              default:
                return {};
            }
          })()
        )
      );
    }
  };

  const propsByTreeType = new Map([
    [
      'hazard',
      {
        checked: showHazardTrees,
        name: HAZARD_TREES,
        description: 'Declining trees with strike potential',
      },
    ],
    [
      'declining',
      {
        checked: showDecliningTrees,
        name: DECLINING_TREES,
        description: 'Declining trees without strike potential',
      },
    ],
    [
      'strike',
      {
        checked: showStrikeTrees,
        name: STRIKE_TREES,
        description: 'Other trees with strike potential',
      },
    ],
  ]);

  return (
    <div className={classes.groupWrapper}>
      <div>
        <div className={classes.groupTitle}>{title}</div>
        {description && (
          <div className={classes.groupDescription}>{description}</div>
        )}
      </div>
      {treeTypes.map((t) => {
        const props = propsByTreeType.get(t);
        const name = props?.name;
        const labelText = getClientTerm(`map.layerManager.${t}TreesTitle`);
        const inputId = `fall-in-${t}`;
        return (
          <div key={t} className={classes.row}>
            <div className={classes.icon}>
              <Icon name={`${capitalize(t)}Trees`} size={16} />
            </div>
            <Text
              component="label"
              htmlFor={inputId}
              fw={500}
              size="md"
              className={classes.label}
            >
              {labelText}
            </Text>
            <Text size="sm" fw={300} c="dimmed" className={classes.description}>
              {props?.description}
            </Text>
            <Checkbox
              id={inputId}
              checked={!!props?.checked}
              name={name}
              onChange={(e) => setOption(e.currentTarget.checked, name)}
              size="sm"
              aria-label={labelText}
              className={classes.checkbox}
            />
          </div>
        );
      })}
    </div>
  );
};
